import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import PartnerCommissionApi from "../../../../infrastructure/api/partnerCommissionApi";
import { getEnumLabel, TRIGGER_EVENT_LABELS } from "../../SLN/market-growth/constants/commissionEnums";
import {
  PARTNER_COMMISSION_STATUS_LABELS,
  getCommissionStatusChipColor,
} from "../constants/portalEnums";
import { formatMoney } from "../utils/formatMoney";

const Field = ({ label, value }) => (
  <>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mb: 1.5 }}>
      {value ?? "—"}
    </Typography>
  </>
);

const CommissionDetailDialog = ({ open, onClose, commissionId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!commissionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await PartnerCommissionApi.getCommissionDetail(commissionId);
      const normalized = PartnerCommissionApi.normalize(
        res,
        "بارگذاری جزئیات پورسانت ناموفق بود."
      );
      if (normalized.error) {
        setError(normalized.error);
        setData(null);
        return;
      }
      setData(normalized.data);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [commissionId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>جزئیات پورسانت #{commissionId}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : data ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field
                  label="رویداد"
                  value={getEnumLabel(TRIGGER_EVENT_LABELS, data.triggerEvent)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  وضعیت
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Chip
                    size="small"
                    label={getEnumLabel(PARTNER_COMMISSION_STATUS_LABELS, data.status)}
                    color={getCommissionStatusChipColor(data.status)}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="پروژه" value={data.relatedProjectId} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="گروه پورسانتی" value={data.commissionGroupTitle} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="قانون" value={data.ruleTitle} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="مبلغ مبنا" value={formatMoney(data.baseAmount)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="مبلغ پورسانت" value={formatMoney(data.commissionAmount)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  label="تاریخ ایجاد"
                  value={
                    data.createdAt
                      ? new Date(data.createdAt).toLocaleDateString("fa-IR")
                      : "—"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Field label="توضیحات" value={data.description} />
              </Grid>
              {data.parentCommissionId ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <Field label="پورسانت والد" value={data.parentCommissionId} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      label="مبلغ پورسانت والد"
                      value={formatMoney(data.parentCommissionAmount)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="سهم تقسیم‌شده" value={formatMoney(data.childSplitAmount)} />
                  </Grid>
                </>
              ) : null}
            </Grid>

            {data.childSplits?.length ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  تقسیم به زیرمجموعه
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>شناسه نماینده</TableCell>
                      <TableCell>مبلغ</TableCell>
                      <TableCell>وضعیت</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.childSplits.map((split) => (
                      <TableRow key={split.partnerId}>
                        <TableCell>{split.partnerId}</TableCell>
                        <TableCell>{formatMoney(split.commissionAmount)}</TableCell>
                        <TableCell>
                          {getEnumLabel(PARTNER_COMMISSION_STATUS_LABELS, split.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : null}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>بستن</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommissionDetailDialog;
